// tslint:disable:no-commented-code
// tslint:disable:no-duplicate-string
// tslint:disable:only-arrow-functions

import { SimpleSelectOptionType } from '@epicuro-next/features/landing-panel';
import {
  IDynamicBindableValue,
  IDynamicInput,
  IDynamicItem,
  IDynamicSimpleText,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';

import {
  createInstance,
  createInstanceWithChildren,
  getIdProvider,
} from '../../../../utils/dynamic-items-utils';

import {
  createSquarePanelFromRecommendations,
  ids as templateIds,
  propertyNames as templatePropertyNames,
} from './../square-panel-from-recommendations';
import {
  createApiRequestPayloadPropertiesProvider,
  getApiResponseTransformMethod,
} from './best-performing-articles-helpers';

const propertyNames = {
  ...templatePropertyNames,
  filterMissingImages: 'filterMissingImages',
  selectedDayRange: 'selectedDayRange',
  orderType: 'orderType',
  nrOfProductsToShow: 'nrOfProductsToShow',
  brandOrganisationId: 'brandOrganisationId',
};

export const squarePanelFromRecommendationsBestPerformingArticles =
  createSquarePanelFromRecommendationsBestPerformingArticles().styleWrapper;

export function createSquarePanelFromRecommendationsBestPerformingArticles() {
  const bestPerformingArticlesDayRangeSelectId =
    'best-performing-articles-day-range-select';

  const days = [
    { name: '7 days', value: '7' },
    { name: '14 days', value: '14' },
    { name: '30 days', value: '30' },
  ];
  const defaultSelectedDayIndex = 0;

  const dayRangeSelectOptions: SimpleSelectOptionType[] = [];
  for (const day of days) {
    dayRangeSelectOptions.push({
      value: day.value,
      displayValue: day.name,
    });
  }

  const defaultSelectedDayOption: SimpleSelectOptionType =
    dayRangeSelectOptions[defaultSelectedDayIndex];
  const periodInDaysProvider: IDynamicBindableValue = {
    ...getIdProvider(
      bestPerformingArticlesDayRangeSelectId,
      'selectionChanged',
    ),
    startWith: {
      value: defaultSelectedDayOption,
    },
    transform: function (option: SimpleSelectOptionType): string {
      return option.value;
    }.toString(),
  };

  const apiRequestPayloadPropertiesProvider =
    createApiRequestPayloadPropertiesProvider({
      datePeriodDays: periodInDaysProvider,
      orderType: getIdProvider(
        templateIds.squarePanelRoot,
        propertyNames.orderType,
      ),
      filterMissingImages: getIdProvider(
        templateIds.squarePanelRoot,
        propertyNames.filterMissingImages,
      ),
      brandOrganisationId: getIdProvider(
        templateIds.squarePanelRoot,
        propertyNames.brandOrganisationId,
      ),
    });

  const template = createSquarePanelFromRecommendations(
    'LandingPage',
    {
      apiRequestPayloadPropertiesProvider,
    },
    getApiResponseTransformMethod(),
  );

  addAdditionalArguments(template.root);

  const simpleDayRangeSelect = createInstance({
    id: bestPerformingArticlesDayRangeSelectId,
    contentTypeId: 'dynamic-elements-simple-select',
    inputs: {
      options: dayRangeSelectOptions,
      selectedValue: getIdProvider(
        template.root.id,
        propertyNames.selectedDayRange,
      ),
    },
  });

  // This ping pong to root and simpleDayRangeSelect is needed as id providing does not work on an element of itself.
  (template.root.data as { [key: string]: IDynamicInput })[
    propertyNames.selectedDayRange
  ] = periodInDaysProvider;

  const firstPartOfHeaderText: IDynamicSimpleText = {
    contentTypeId: 'dynamic-elements-simple-text',
    styles: {
      style: {
        'white-space': 'pre',
      },
      variables: {
        '--text-dynamic-color': 'var(--dark-mode-gray-600, #414141)',
      },
    },
    inputs: {
      text: {
        ...getIdProvider(template.root.id, propertyNames.headerText),
      },
    },
  };

  const spaceBetweenPrefixAndDaysSelection: IDynamicSimpleText = {
    id: 'best-performing-articles-white-psace-between-prefix-and-selection',
    contentTypeId: 'dynamic-elements-simple-text',
    styles: {
      style: {
        'white-space': 'pre',
      },
      variables: {
        '--text-dynamic-color': 'var(--dark-mode-gray-600, #414141)',
      },
    },
    inputs: {
      text: ' ',
    },
  };

  const nrOfDaysText: IDynamicSimpleText = {
    id: 'best-performing-articles-nr-of-days-text',
    contentTypeId: 'dynamic-elements-simple-text',
    styles: {
      style: {
        'text-decoration': 'underline',
      },
      variables: {
        '--text-dynamic-color': 'var(--dark-mode-gray-600, #414141)',
      },
      classes: ['cursor-pointer'],
    },
    inputs: {
      text: periodInDaysProvider,
    },
  };

  const dropdownForDaysPeriod: IDynamicItem = {
    contentTypeId: 'dynamic-elements-dropdown-wrapper',
    inputs: {
      triggerContent: nrOfDaysText,
      dropdownContent: simpleDayRangeSelect,
    },
  };

  const daysText: IDynamicSimpleText = {
    contentTypeId: 'dynamic-elements-simple-text',
    styles: {
      style: {
        'white-space': 'pre',
      },
      variables: {
        '--text-dynamic-color': 'var(--dark-mode-gray-600, #414141)',
      },
    },
    inputs: {
      text: ' days',
    },
  };

  const itemsInHeaderWrapper = createInstanceWithChildren({
    id: 'best-performing-articles-header-wrapper',
    contentTypeId: 'dynamic-collections-renderer',
    styles: {
      classes: ['d-flex', 'flex-row', 'justify-content-center', 'h2', 'mb-0'],
      variables: {
        '--text-dynamic-color': 'var(--dark-mode-gray-600, #414141)',
        '--text-dynamic-font-size': '1.5rem',
      },
    },
    children: [
      firstPartOfHeaderText,
      spaceBetweenPrefixAndDaysSelection,
      dropdownForDaysPeriod,
      daysText,
    ],
  });

  template.headerContainer.children = [itemsInHeaderWrapper];
  return { ...template };
}

function addAdditionalArguments(element: { data: { [key: string]: unknown } }) {
  element.data[propertyNames.orderType] = 0;
  element.data[propertyNames.headerText] = 'US Re-Ordered in the last';
  element.data[propertyNames.filterMissingImages] = false;
  element.data[propertyNames.nrOfProductsToShow] = 9;
}
